import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Bolt as BoltIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Power as PowerIcon,
  Battery20 as BatteryIcon,
  Business as BusinessIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { DashboardService, SessionService, ChargerService } from '../../services';
import { getConnectorStatusColor, getConnectorStatusIcon } from '../../utils';
import { useSelector } from 'react-redux';
import { selectSelectedTeam } from '../../store/authSlice';

const DashboardTab = () => {
  const selectedTeam = useSelector(selectSelectedTeam);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [chargersLoading, setChargersLoading] = useState(true);
  const [stats, setStats] = useState({ 
    availableCount: 0, 
    chargingCount: 0, 
    faultedCount: 0, 
    preparingCount: 0,
  });
  const [activeSessionsData, setActiveSessionsData] = useState([]);
  const [sessionsPagination, setSessionsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 3
  });
  const [sessionsLoading, setSessionsLoading] = useState(false);
  //const [alerts, setAlerts] = useState([]);
  const [chargers, setChargers] = useState([]);

  const fetchChargers = useCallback(async () => {
    if (!selectedTeam?.teamId) {
      setChargersLoading(false);
      return;
    }
    
    setChargersLoading(true);
    try {
      const teamParams = { 
        teamId: selectedTeam.teamId,
        teamType: selectedTeam.teamType 
      };
      
      const chargersResponse = await ChargerService.getAllChargers({ 
        ...teamParams, 
        page: 1, 
        limit: 10 
      });
      
      if (chargersResponse.success) {
        setChargers(chargersResponse.data?.data?.chargers || []);
      }
    } catch (error) {
      console.error('Error fetching chargers independently:', error);
    } finally {
      setChargersLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType]);

  const fetchActiveSessions = useCallback(async (page = 1) => {
    if (!selectedTeam?.teamId) return;
    
    setSessionsLoading(true);
    try {
      const teamParams = {
        teamId: selectedTeam.teamId,
        teamType: selectedTeam.teamType,
        page: page,
        limit: sessionsPagination.itemsPerPage
      };
      
       const sessionsResponse = await SessionService.getActiveSessions(teamParams);
      if (sessionsResponse.success) {
        // Use mock data for now, replace with real API response
        const mockSessions = sessionsResponse?.data?.data.slice((page - 1) * sessionsPagination.itemsPerPage, page * sessionsPagination.itemsPerPage);
        setActiveSessionsData(mockSessions);
        
        setSessionsPagination(prev => ({
          ...prev,
          currentPage: page,
          totalItems: sessionsResponse?.data?.data.length,
          totalPages: Math.ceil(sessionsResponse?.data?.data.length / prev.itemsPerPage)
        }));
      }
    } catch (error) {
      console.error('Error fetching active sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType, sessionsPagination.itemsPerPage]);

  const fetchDashboardData = useCallback(async () => {
    // Don't fetch if no team/company is selected
    if (!selectedTeam?.teamId) {
      setLoading(false);
      setStatsLoading(false);
      setChargersLoading(false);
      return;
    }

    setLoading(true);
    setStatsLoading(true);
    setChargersLoading(true);
    try {
      // Build query parameters based on selected team
      const teamParams = { 
        teamId: selectedTeam.teamId,
        teamType: selectedTeam.teamType 
      };
      
      // Fetch stats and chargers independently for better UX
      const fetchStats = async () => {
        try {
          const statsResponse = await DashboardService.getStats(teamParams);
          if (statsResponse.success) {
            setStats(statsResponse?.data?.data);
          }
        } catch (error) {
          console.error('Error fetching stats:', error);
        } finally {
          setStatsLoading(false);
        }
      };

      // Run stats and sessions fetches in parallel
      await Promise.all([
        fetchStats(),
        fetchActiveSessions(1)
      ]);
      
      // Fetch chargers independently
      fetchChargers();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType, fetchActiveSessions, fetchChargers]);

  useEffect(() => {
    // Only fetch data if a team or company is selected
    if (selectedTeam?.teamId) {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, fetchDashboardData]);

  const handlePageChange = (event, newPage) => {
    fetchActiveSessions(newPage);
  };

  // Skeleton components for loading states
  const StatsGridSkeleton = () => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
      {[1, 2, 3, 4].map((item) => (
        <Card key={item} sx={{ position: 'relative', overflow: 'hidden' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Skeleton variant="text" width={60} height={48} sx={{ fontSize: '2rem' }} />
                <Skeleton variant="text" width={120} height={20} />
              </Box>
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const SessionsSkeleton = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="rounded" width={40} height={20} />
        </Box>
        {[1, 2, 3].map((item) => (
          <Box key={item} sx={{ borderLeft: '4px solid #e0e0e0', pl: 2, py: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="text" width={200} height={16} />
                <Skeleton variant="text" width={120} height={14} />
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Skeleton variant="rounded" width={60} height={24} sx={{ mb: 0.5 }} />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Skeleton variant="text" width={100} height={12} />
              <Skeleton variant="text" width={80} height={12} />
              <Skeleton variant="text" width={60} height={12} />
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );

  const ChargerGridSkeleton = () => (
    <Card>
      <CardContent>
        <Skeleton variant="text" width={180} height={24} sx={{ mb: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
            <Box key={item} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 1 }}>
              <Skeleton variant="rounded" width={50} height={50} />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Skeleton variant="circular" width={12} height={12} />
                <Skeleton variant="circular" width={12} height={12} />
              </Box>
              <Skeleton variant="text" width={60} height={14} />
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  // Show message if no team or company is selected
  if (!selectedTeam?.teamId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Select a Team or Company
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please select a team or company from the dropdown above to view dashboard data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Modern Stats Grid */}
      {statsLoading ? (
        <StatsGridSkeleton />
      ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
        <Card sx={{ 
          background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
          border: '1px solid rgba(46, 125, 50, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" fontWeight="700" color="success.dark">
                  {stats.availableCount}
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="600">
                  Available Connectors
                </Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ 
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" fontWeight="700" color="primary.dark">
                  {stats.chargingCount}
                </Typography>
                <Typography variant="body2" color="primary.main" fontWeight="600">
                  Charging Connectors
                </Typography>
              </Box>
              <BoltIcon sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ 
          background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
          border: '1px solid rgba(211, 47, 47, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" fontWeight="700" color="error.dark">
                  {stats.faultedCount}
                </Typography>
                <Typography variant="body2" color="error.main" fontWeight="600">
                  Faulted Connectors
                </Typography>
              </Box>
              <WarningIcon sx={{ fontSize: 32, color: 'error.main', opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
          border: '1px solid rgba(156, 39, 176, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h3" fontWeight="700" color="secondary.dark">
                  {stats.preparingCount}
                </Typography>
                <Typography variant="body2" color="secondary.main" fontWeight="600">
                  Preparing Connectors
                </Typography>
              </Box>
              <PowerIcon sx={{ fontSize: 32, color: 'secondary.main', opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>
      )}

      {/* Active Sessions */}
      {loading && sessionsLoading ? (
        <SessionsSkeleton />
      ) : (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BoltIcon color="primary" />
              <Typography variant="subtitle2" fontWeight="600">
                Active Sessions
              </Typography>
              <Chip 
                size="small" 
                label={sessionsPagination.totalItems} 
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            </Box>
          </Box>
          
          {sessionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : activeSessionsData.length > 0 ? (
            <>
              {activeSessionsData.map((session, index) => (
                <Box key={session.id}>
                  <Box sx={{ 
                    borderLeft: '4px solid', 
                    borderLeftColor: 'primary.main',
                    pl: 2,
                    py: 1
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          {session.chargerName}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.85rem' }}>
                          {session.vehicle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Started: {new Date(session.startTime).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          size="small" 
                          label={`${session.soc || 0}%`} 
                          variant="outlined"
                          icon={<BatteryIcon />}
                          sx={{ mb: 0.5 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Energy Consumed: {session.energyConsumed} kWh
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration: {session.duration}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Power: {session.maxPower} kW
                      </Typography>
                    </Box>
                  </Box>
                  {index < activeSessionsData.length - 1 && <Divider sx={{ my: 1 }} />}
                </Box>
              ))}
              
              {/* Pagination */}
              {sessionsPagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 1 }}>
                  <IconButton 
                    size="small"
                    onClick={() => handlePageChange(null, sessionsPagination.currentPage - 1)}
                    disabled={sessionsPagination.currentPage === 1}
                  >
                    <ChevronLeft />
                  </IconButton>
                  
                  <Typography variant="body2" color="text.secondary">
                    {sessionsPagination.currentPage} of {sessionsPagination.totalPages}
                  </Typography>
                  
                  <IconButton 
                    size="small"
                    onClick={() => handlePageChange(null, sessionsPagination.currentPage + 1)}
                    disabled={sessionsPagination.currentPage === sessionsPagination.totalPages}
                  >
                    <ChevronRight />
                  </IconButton>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
              No active sessions found
            </Typography>
          )}
        </CardContent>
      </Card>
      )}

      {/* Live Status Grid */}
      {chargersLoading ? (
        <ChargerGridSkeleton />
      ) : (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PowerIcon color="primary" />
            <Typography variant="subtitle2" fontWeight="600">
              Live Status Grid
            </Typography>
            <Chip 
              size="small" 
              label={chargers.length || 0} 
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Box>
          {chargers.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: 2 
            }}>
              {chargers.map((charger) => {
              const connectorTooltip = (
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'white', fontWeight: 600 }}>
                    {charger.chargepoint_id || charger.id}
                  </Typography>
                  {charger.connectors?.map((connector, index) => (
                    <Box key={connector.connector_pk || index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box sx={{ 
                        color: getConnectorStatusColor(connector.status),
                        fontSize: 16,
                        fontWeight: 'bold'
                      }}>
                        {getConnectorStatusIcon(connector.status)}
                      </Box>
                      <Typography variant="body2" sx={{ color: 'white' }}>
                        {connector.connectorName || `C${connector.connectorId}`}: 
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: getConnectorStatusColor(connector.status), 
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {connector.status}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );

              return (
                <Tooltip 
                  key={charger.chargepoint_pk || charger.id}
                  title={connectorTooltip}
                  arrow
                  placement="top"
                  disableHoverListener={false}
                  disableFocusListener={false}
                  disableTouchListener={false}
                  enterTouchDelay={0}
                  leaveTouchDelay={60000}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      bgcolor: 'rgba(0, 0, 0, 0.9)',
                      maxWidth: 300,
                      fontSize: '0.875rem',
                      zIndex: 9999
                    }
                  }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      gap: 1,
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                        transform: 'translateY(-2px)'
                      },
                      '&:active': {
                        transform: 'scale(0.95)'
                      }
                    }}
                  >
                    {/* Main charger status box */}
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: charger.connectors?.[0] ? getConnectorStatusColor(charger.connectors[0].status) : '#9e9e9e',
                      color: 'white',
                      position: 'relative',
                      boxShadow: 2
                    }}>
                      <PowerIcon fontSize="medium" />
                    </Box>
                    
                    {/* Connector status indicators */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5,
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexWrap: 'wrap'
                    }}>
                      {charger.connectors?.map((connector, index) => (
                        <Box
                          key={connector.connector_pk || index}
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: getConnectorStatusColor(connector.status),
                            border: '1px solid rgba(255,255,255,0.8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '8px',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        >
                          {connector.connectorId}
                        </Box>
                      ))}
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: '11px', 
                        textAlign: 'center',
                        fontWeight: 500,
                        maxWidth: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {charger.chargepoint_id || charger.id}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              No chargers available
            </Typography>
          )}
        </CardContent>
      </Card>
      )}
    </Box>
  );
};

export default DashboardTab;